import React, { useEffect, useRef, useState } from 'react';
import { DateUtils } from '../../../utils/dateUtils';
import { endPoints } from '../../../utils/endpoints';
import { makeDeleteCall, makeGetCall, makePatchCall } from '../../../effects/requests';
import { ApiResponseType } from '../../../types';
import { emptyFunction, groupByEpisode, showToast } from '../../../utils';
import SceneCard from './createScript/SceneCard';

interface Props {
    onClose: () => void;
    onRefresh: () => void;
    srciptDetails: any
}

const ResponsiveImage = ({
    src,
    alt = "Image",
    fit = "contain",
    aspectRatio = "16 / 9",
    onEdit,
    onDelete
}: any) => {
    return (
        <div
            style={{
                width: "100%",
                aspectRatio,
                overflow: "hidden",
                marginBottom: 20,
                borderRadius: '12px',
                background: '#000',
                position: 'relative'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, position: 'absolute', right: 10, top: 10 }}>
                <button onClick={onEdit} type="button" className="cs-icon-btn cs-icon-btn--edit" aria-label="Edit scene">
                    <img src="/assets/images/edit-pencil-icon.svg" alt="edit" />
                </button>
                {/* <button onClick={onDelete} type="button" className="cs-icon-btn cs-icon-btn--delete" aria-label="Delete scene">
                    <img src="/assets/images/trash-red-icon.svg" alt="delete" />
                </button> */}
            </div>
            <img
                src={src}
                alt={alt}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: fit,
                    objectPosition: "center",
                    display: "block",
                }}
            />
        </div>
    );
};

const SrciptDetails: React.FC<Props> = ({ onClose, srciptDetails, onRefresh }) => {

    const fileRef: any = useRef(null);
    const [allData, setAllData] = useState<any>([])
    const [coverImage, setCoverImage] = useState(srciptDetails?.cover_image_url || null)
    const [expandedEpisodes, setExpandedEpisodes] = useState([]);

    const fetchScriptScene = () => {
        const url = `${endPoints.LOCATION_SCRIPT_SCENE}?script_id=${srciptDetails?.id}`
        makeGetCall(url).then((res: ApiResponseType<any>) => {
            if (res?.status?.code === 200) {
                const groupedEpisodes = groupByEpisode(res?.results?.length > 0 ? res?.results : []);
                setAllData(groupedEpisodes);
            }
        }).catch(emptyFunction)
    }

    const handleDelete = (selectedId: any) => {
        const url = `${endPoints.LOCATION_SCRIPT_SCENE}${selectedId}`
        makeDeleteCall(url).then((res: ApiResponseType<any>) => {
            if (res?.status?.code === 200) {
                showToast(res?.status?.message, 'success');
                fetchScriptScene();
                onRefresh?.()
            }
        }).catch(emptyFunction)
    }

    const handleUpdate = (img: any, isDelete = false) => {
        const fd = new FormData();
        fd.append('cover_image', img);
        const url = `${endPoints.LOCATION_SCRIPT}${srciptDetails?.id}`
        makePatchCall(url, fd).then((res: ApiResponseType<any>) => {
            if (res.status?.code == 200) {
                showToast(isDelete ? 'Deleted successfully!' : 'Updated successfully!', 'success');
                setCoverImage(res?.data?.cover_image_url || null)
            }
        }).catch(emptyFunction)
    }

    const handleFileChange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) handleUpdate?.(file, false);
    }

    const toggleEpisode = (episodeId: any) => {
        setExpandedEpisodes((prev: any) =>
            prev.includes(episodeId)
                ? prev.filter((id: any) => id !== episodeId)
                : [...prev, episodeId]
        );
    };

    useEffect(() => {
        if (srciptDetails?.id)
            fetchScriptScene();
    }, [srciptDetails?.id]);

    return (
        <>
            {/* Backdrop */}
            <div className="cl-backdrop" />

            {/* Drawer panel */}
            <div className="cl-drawer">

                {/* Header */}
                <div className="cl-drawer__header">
                    <button className="cl-drawer__close" onClick={onClose}>
                        <img src='/assets/images/dismiss.svg' alt='close' />
                    </button>
                    <h2 className="cl-drawer__title">Script Details</h2>
                </div>

                {/* Main content */}
                <div className="cl-drawer__body">
                    <ResponsiveImage
                        src={coverImage || '/assets/images/yellow-6th.png'}
                        fit="stretch"
                        aspectRatio="16 / 9"
                        onDelete={() => handleUpdate?.('', true)}
                        onEdit={() => fileRef.current?.click()}
                    />
                    <div style={{ marginBottom: 20 }} className="li-card">
                        <h3 className="li-card__heading">Script Information</h3>
                        <div className="li-field">
                            <div className="li-field__icon-wrap li-field__icon-wrap--purple">
                                <img src="/assets/images/perscript-active.svg" alt="" />
                            </div>
                            <div className="li-field__content">
                                <span className="li-field__label">Script name</span>
                                <span className="li-field__value">
                                    {srciptDetails?.name || 'NA'}
                                </span>
                            </div>
                        </div>
                        <div className="li-field">
                            <div className="li-field__icon-wrap li-field__icon-wrap--purple">
                                <img src="/assets/images/contact-person-icon.svg" alt="" />
                            </div>
                            <div className="li-field__content">
                                <span className="li-field__label">Created By</span>
                                <span className="li-field__value">{srciptDetails?.production_company_name || ''}</span>
                            </div>
                        </div>
                        <div className="li-field">
                            <div className="li-field__icon-wrap li-field__icon-wrap--purple">
                                <img src="/assets/images/view-meta.svg" alt="" />
                            </div>
                            <div className="li-field__content">
                                <span className="li-field__label">Number of Scenes</span>
                                <span className="li-field__value">{srciptDetails?.number_of_episodes || srciptDetails?.number_of_scenes || '0'}</span>
                            </div>
                        </div>
                        <div className="li-field">
                            <div className="li-field__icon-wrap li-field__icon-wrap--purple">
                                <img src="/assets/images/calendar-icon.svg" alt="" />
                            </div>
                            <div className="li-field__content">
                                <span className="li-field__label">Date created</span>
                                <span className="li-field__value">{srciptDetails?.created_at ? `${DateUtils.formatDateTime(srciptDetails?.created_at, 'DD MMMM YYYY')}` : ''}</span>
                            </div>
                        </div>
                        {srciptDetails?.add_locations_to_vault &&
                            <>
                                <div className="li-field">
                                    <div className="li-field__icon-wrap li-field__icon-wrap--purple">
                                        <img src="/assets/images/location-pin-icon.svg" alt="" />
                                    </div>
                                    <div className="li-field__content">
                                        <span className="li-field__label">Added to location vault</span>
                                        <span className="li-field__value">Yes</span>
                                    </div>
                                </div>
                                <div className="li-field">
                                    <div className="li-field__icon-wrap li-field__icon-wrap--purple">
                                        <img src="/assets/images/calendar-icon.svg" alt="" />
                                    </div>
                                    <div className="li-field__content">
                                        <span className="li-field__label">Date scouted</span>
                                        <span className="li-field__value">{srciptDetails?.locations_date ? `${DateUtils.formatDateTime(srciptDetails?.locations_date, 'DD MMMM YYYY')}` : ''}</span>
                                    </div>
                                </div>
                            </>
                        }
                    </div>

                    {allData.length > 0 && (
                        <>
                            {srciptDetails?.show_type === "tv_show"
                                ?
                                <>
                                    {allData?.map((episode: any) => {
                                        const isExpanded = expandedEpisodes.includes(episode?.episode_id as never);
                                        return (
                                            <div key={episode?.episode_id}
                                                style={{
                                                    border: '1px solid #e8e8f0',
                                                    borderRadius: '12px',
                                                    padding: '0px 15px 0px 15px',
                                                    marginBottom: '10px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        marginBottom: '15px',
                                                        marginTop: '15px'
                                                    }}
                                                    key={episode?.episode_id}
                                                    className="setting-policy-design d-flex align-items-center justify-content-between"
                                                    onClick={() => toggleEpisode(episode.episode_id)}
                                                >
                                                    <div className="d-flex gap-4 align-items-center">
                                                        <a className="a_tag_style" href='#'>Episode {episode?.episode_number}</a>
                                                    </div>
                                                    <img style={{ transform: isExpanded ? "rotate(90deg)" : 'rotate(0deg)' }} src={"/assets/images/play-icon-dark.svg"} alt="" />
                                                </div>
                                                {isExpanded && (
                                                    <div className="cs-scene-list">
                                                        {episode?.scenes?.map((item: any) => (
                                                            <SceneCard
                                                                key={item.id}
                                                                scene={item}
                                                                onEdit={emptyFunction}
                                                                onDelete={() => handleDelete(item?.id)}
                                                                hideEdit
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </>
                                :
                                <>
                                    <div className="cs-scene-list">
                                        {allData.map((item: any) => (
                                            <SceneCard
                                                key={item.id}
                                                scene={item}
                                                onEdit={emptyFunction}
                                                onDelete={() => handleDelete(item?.id)}
                                                hideEdit
                                            />
                                        ))}
                                    </div>
                                </>
                            }
                        </>
                    )}
                </div>
            </div>
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
            />
        </>
    );
};

export default SrciptDetails;